import * as React from "react";
import { toast } from "sonner";
import { Megaphone, Check, Plus, ChevronLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import DetailDrawer from "@/components/DetailDrawer";

/** BroadcastsDrawer — full experience:
 *   - List of broadcasts w/ ack indicator per row (mine) and ack count (managers)
 *   - Click a row → detail: subject + body + ack roster (managers)
 *   - Managers get a Compose button that opens a nested form
 *   - Non-managers see an Acknowledge button when applicable
 */
export default function BroadcastsDrawer({ open, onOpenChange, companyId, sites = [], canManage }) {
  const [rows, setRows] = React.useState(null);
  const [view, setView] = React.useState("list"); // list | detail | compose
  const [active, setActive] = React.useState(null);
  const [roster, setRoster] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!open) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/comms/broadcasts`);
      setRows(data.broadcasts || []);
    } catch (e) {
      setRows([]);
    }
  }, [companyId, open]);

  React.useEffect(() => { if (open) { load(); setView("list"); setActive(null); setRoster(null); } }, [open, load]);
  // Refresh on relevant SSE
  React.useEffect(() => {
    if (!open) return;
    const on = (e) => { const t = e.detail?.type; if (t && t.startsWith("broadcast.")) load(); };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [open, load]);

  async function openDetail(bc) {
    setActive(bc);
    setView("detail");
    if (canManage) {
      try {
        const { data } = await api.get(`/companies/${companyId}/comms/broadcasts/${bc.id}/acks`);
        setRoster(data);
      } catch (e) {
        setRoster(null);
      }
    }
  }

  async function ack(bc) {
    try {
      await api.post(`/companies/${companyId}/comms/broadcasts/${bc.id}/ack`);
      toast.success("Acknowledged");
      load();
      if (active?.id === bc.id) openDetail({ ...bc, my_ack: { acked_at: new Date().toISOString() } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={view === "compose" ? "New broadcast" : view === "detail" ? (active?.subject || "Broadcast") : "Broadcasts"}
      subtitle={view === "compose" ? "Company or site-wide announcement"
                                    : view === "detail" ? "Detail + ack roster" : "Company announcements"}
      testid="broadcasts-drawer"
    >
      {view === "list" && (
        <div className="p-5 space-y-3" data-testid="broadcasts-list">
          {canManage && (
            <button className="btn-violet" onClick={() => setView("compose")}
                    data-testid="broadcast-compose-open">
              <Plus size={13} /> Compose broadcast
            </button>
          )}
          {rows === null && <div className="text-[12px] text-brand-muted py-3">Loading…</div>}
          {rows?.length === 0 && (
            <div className="text-[12px] text-brand-muted py-3">No broadcasts yet.</div>
          )}
          {rows?.map((r) => (
            <div key={r.id}
                 className="card-panel !py-2 !px-3 cursor-pointer hover:border-brand-violet/40 transition-colors"
                 onClick={() => openDetail(r)}
                 data-testid={`broadcast-row-${r.id}`}>
              <div className="flex items-center gap-2">
                <Megaphone size={13} className="text-brand-violet shrink-0" />
                <span className="text-[13px] font-semibold text-brand-ink truncate">{r.subject}</span>
                {r.my_ack ? (
                  <span className="chip chip-emerald ml-auto shrink-0">acked</span>
                ) : r.requires_ack ? (
                  <span className="chip chip-amber ml-auto shrink-0">needs ack</span>
                ) : null}
              </div>
              <div className="mt-1 text-[11.5px] text-brand-muted flex items-center gap-2">
                <span>{r.sender_name || "System"}</span>
                <span>·</span>
                <span className="mono-num">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                {canManage && (
                  <>
                    <span>·</span>
                    <span className="mono-num">{r.ack_count}/{r.recipient_count} acked</span>
                  </>
                )}
                {r.target_kind === "site" && (
                  <>
                    <span>·</span>
                    <span className="chip">site</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "detail" && active && (
        <div className="p-5 space-y-4" data-testid="broadcast-detail">
          <button className="btn-ghost !px-2 !h-7 -ml-2" onClick={() => setView("list")}
                  data-testid="broadcast-back">
            <ChevronLeft size={13} /> Back
          </button>
          <div>
            <div className="micro-label">From</div>
            <div className="text-[13px] text-brand-ink">{active.sender_name || "System"}</div>
          </div>
          <div>
            <div className="micro-label">Body</div>
            <div className="mt-1 text-[13px] text-brand-ink whitespace-pre-wrap">{active.body}</div>
          </div>
          {active.requires_ack && !active.my_ack && (
            <button className="btn-violet w-full" onClick={() => ack(active)}
                    data-testid="broadcast-ack-btn">
              <Check size={13} /> Acknowledge
            </button>
          )}
          {active.my_ack && (
            <div className="text-[11.5px] text-brand-emerald mono-num" data-testid="broadcast-my-ack">
              You acknowledged at {(active.my_ack.acked_at || "").slice(0, 16).replace("T", " ")}
            </div>
          )}
          {canManage && roster && (
            <div className="pt-3 border-t border-brand-rule">
              <div className="micro-label mb-2">Ack roster</div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <RosterCell label="Recipients" value={roster.totals.recipients} testid="broadcast-tot-recipients" />
                <RosterCell label="Acked" value={roster.totals.acked} testid="broadcast-tot-acked" />
              </div>
              {roster.pending?.length > 0 && (
                <div>
                  <div className="micro-label mb-1">Still waiting</div>
                  <div className="space-y-1">
                    {roster.pending.slice(0, 10).map((u) => (
                      <div key={u.user_id} className="flex items-center gap-2 text-[12px]"
                           data-testid={`broadcast-pending-${u.user_id}`}>
                        <span className="h-2 w-2 rounded-full bg-brand-coral"></span>
                        <span className="text-brand-ink">{u.name || u.email || u.user_id.slice(0, 8)}</span>
                      </div>
                    ))}
                    {roster.pending.length > 10 && (
                      <div className="text-[11px] text-brand-muted">+{roster.pending.length - 10} more…</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {view === "compose" && canManage && (
        <ComposeForm companyId={companyId} sites={sites}
                     onDone={() => { load(); setView("list"); }}
                     onBack={() => setView("list")} />
      )}
    </DetailDrawer>
  );
}

function RosterCell({ label, value, testid }) {
  return (
    <div className="p-2 rounded-md bg-brand-hush border border-brand-rule" data-testid={testid}>
      <div className="micro-label">{label}</div>
      <div className="mt-0.5 text-[15px] font-bold mono-num text-brand-ink">{value}</div>
    </div>
  );
}

function ComposeForm({ companyId, sites, onDone, onBack }) {
  const [form, setForm] = React.useState({
    subject: "", body: "", target_kind: "company", target_site_id: "",
    requires_ack: true, unack_alert_hours: 24,
  });
  const [busy, setBusy] = React.useState(false);

  async function send() {
    if (!form.subject.trim() || !form.body.trim() || busy) return;
    setBusy(true);
    try {
      const payload = {
        subject: form.subject, body: form.body,
        target_kind: form.target_kind,
        target_site_id: form.target_kind === "site" ? form.target_site_id : null,
        requires_ack: !!form.requires_ack,
        unack_alert_hours: Number(form.unack_alert_hours) || 24,
      };
      await api.post(`/companies/${companyId}/comms/broadcasts`, payload);
      toast.success("Broadcast sent");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return (
    <div className="p-5 space-y-3" data-testid="broadcast-compose">
      <button className="btn-ghost !px-2 !h-7 -ml-2" onClick={onBack}>
        <ChevronLeft size={13} /> Back
      </button>
      <div>
        <label className="micro-label">Subject</label>
        <input className="field-input mt-1" value={form.subject} maxLength={140}
               onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
               data-testid="broadcast-subject" />
      </div>
      <div>
        <label className="micro-label">Body</label>
        <textarea className="field-input mt-1 !h-28" value={form.body} maxLength={4000}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  data-testid="broadcast-body" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="micro-label">Audience</label>
          <select className="field-input mt-1" value={form.target_kind}
                  onChange={(e) => setForm((f) => ({ ...f, target_kind: e.target.value }))}
                  data-testid="broadcast-target-kind">
            <option value="company">Whole company</option>
            <option value="site">Specific site</option>
          </select>
        </div>
        {form.target_kind === "site" && (
          <div>
            <label className="micro-label">Site</label>
            <select className="field-input mt-1" value={form.target_site_id}
                    onChange={(e) => setForm((f) => ({ ...f, target_site_id: e.target.value }))}
                    data-testid="broadcast-target-site">
              <option value="">Pick a site…</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 pt-1">
        <label className="flex items-center gap-1.5 text-[12px] text-brand-ink cursor-pointer">
          <input type="checkbox" className="accent-brand-violet"
                 checked={form.requires_ack}
                 onChange={(e) => setForm((f) => ({ ...f, requires_ack: e.target.checked }))}
                 data-testid="broadcast-requires-ack" />
          Require acknowledgement
        </label>
        {form.requires_ack && (
          <div className="flex items-center gap-1.5 text-[11.5px] text-brand-muted">
            <span>Alert if unacked after</span>
            <input type="number" min="1" max="168"
                   className="field-input !h-7 !w-16 mono-num"
                   value={form.unack_alert_hours}
                   onChange={(e) => setForm((f) => ({ ...f, unack_alert_hours: e.target.value }))}
                   data-testid="broadcast-unack-hours" />
            <span>hours</span>
          </div>
        )}
      </div>
      <button className="btn-violet w-full" onClick={send}
              disabled={busy || !form.subject.trim() || !form.body.trim()
                       || (form.target_kind === "site" && !form.target_site_id)}
              data-testid="broadcast-send">
        <Megaphone size={13} /> {busy ? "Sending…" : "Send broadcast"}
      </button>
    </div>
  );
}
